import { useLocalObservable } from 'mobx-react-lite';
import { CSSProperties, useEffect } from 'react';

import { RemoteImageObject } from '@gem/modules/remote-storage';
import { getAverageColor, hexToRgb, IRGBColor } from '@gem/utils';

import { ChatBackgroundType } from 'APP/constants/appearance';
import { TChatBackground } from 'APP/store/Appearance/Appearance.types';
import { Group } from 'APP/store/Groups/Group';
import Entities from 'STORE';
import { Thread } from 'STORE/Groups/Thread';

interface IChatBackgroundStylesPresenter {
  averageColor: IRGBColor | null;
  groupId: string | null;
  group: Group | null;
  entityIdForBackground: string | null;
  background: TChatBackground;
  backgroundUrl: string | null;
  chatBackgroundStyles: CSSProperties;
  setAverageColor(value: IRGBColor | null): void;
  calculateColor(): Promise<void>;
}

export function useChatBackgroundStyles(): IChatBackgroundStylesPresenter {
  const presenter = useLocalObservable<IChatBackgroundStylesPresenter>(() => ({
    averageColor: null,

    get groupId(): string | null {
      return Entities.GroupsStore.activeGroupId ?? null;
    },

    get group(): Group | null {
      if (!presenter.groupId) {
        return null;
      }
      return Entities.GroupsStore.getGroupById(presenter.groupId);
    },

    get entityIdForBackground(): string | null {
      if (presenter.group && presenter.group instanceof Thread && presenter.group.parentGroup) {
        return presenter.group.parentGroup.id;
      }
      return presenter.groupId;
    },

    get background(): TChatBackground {
      return Entities.appearance.getChatBackgroundForEntity(presenter.entityIdForBackground);
    },

    get backgroundUrl(): string | null {
      if (presenter.background.type === ChatBackgroundType.Pattern) {
        return null;
      }
      if (presenter.background.type === ChatBackgroundType.Wallpaper) {
        return Entities.clientConfig.backgroundsMap[presenter.background.background]?.url || null;
      }
      if (presenter.background.type === ChatBackgroundType.Custom) {
        return (
          Entities.appearance.getCustomChatBackground(presenter.background.background)?.url ?? null
        );
      }
      return null;
    },

    get chatBackgroundStyles(): CSSProperties {
      if (!presenter.averageColor) {
        return {};
      }
      return {
        '--chat-background-average': `rgba(${presenter.averageColor.r}, ${presenter.averageColor.g}, ${presenter.averageColor.b}, .5)`,
      } as CSSProperties;
    },

    setAverageColor(value: IRGBColor | null): void {
      presenter.averageColor = value;
    },

    async calculateColor(): Promise<void> {
      const { backgroundUrl } = presenter;
      if (!backgroundUrl) {
        presenter.setAverageColor(null);
        return;
      }

      if (presenter.background.type === ChatBackgroundType.Wallpaper) {
        const hexColor =
          Entities.clientConfig.backgroundsMap[presenter.background.background].averageColor;
        presenter.setAverageColor(hexToRgb(hexColor));
        return;
      }

      try {
        const img = new RemoteImageObject();

        const color = await new Promise<IRGBColor | null>((resolve) => {
          img.src = backgroundUrl;
          img.onload = (): void => {
            try {
              resolve(getAverageColor(img.image));
            } catch {
              resolve(null);
            }
          };
          img.onerror = (): void => resolve(null);
        });

        presenter.setAverageColor(color);
      } catch {
        presenter.setAverageColor(null);
      }
    },
  }));

  useEffect(() => {
    presenter.calculateColor();
  }, [presenter.backgroundUrl]);

  return presenter;
}
